import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { GitDiff, UploadSimple, ChatText, CheckCircle, CircleNotch } from "@phosphor-icons/react";

export function GapPanel() {
  const qc = useQueryClient();
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [pendingGap, setPendingGap] = useState(null);
  const inputRef = useRef();
  const pyRef = useRef();

  const { data } = useQuery({ queryKey: ["gaps"], queryFn: () => api.get("/portal/gaps").then((r) => r.data) });

  const upload = useMutation({
    mutationFn: ({ file, gap }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("engagement_id", data.engagement_id);
      fd.append("checklist_doc_type", gap.upload_doc_type);
      return api.post("/documents/upload", fd);
    },
    onSuccess: () => { toast.success("Uploaded — the gap will clear once AI files it"); qc.invalidateQueries({ queryKey: ["gaps"] }); qc.invalidateQueries({ queryKey: ["portal"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const sendNote = useMutation({
    mutationFn: (gap) => api.post("/portal/gaps/note", { gap_key: gap.key, note }),
    onSuccess: () => { toast.success("Noted — your CPA will see this in the briefing"); setNoteFor(null); setNote(""); qc.invalidateQueries({ queryKey: ["gaps"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const pyImport = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/portal/py-import", fd); },
    onSuccess: ({ data: d }) => { toast.success(`Return read — ${d.gaps_detected} item${d.gaps_detected === 1 ? "" : "s"} to watch this year`); qc.invalidateQueries({ queryKey: ["gaps"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  if (!data) return null;
  if (!data.has_profile) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-5" data-testid="gap-panel-empty">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><GitDiff size={15} /> Cross-year gap check</h3>
        <p className="text-[11px] text-zinc-500 mt-1 mb-3">Upload last year's filed return (PDF) and we'll automatically flag anything from last year that's missing this year — no redundant requests.</p>
        <button data-testid="gap-py-upload-btn" disabled={pyImport.isPending} onClick={() => pyRef.current?.click()}
          className="w-full rounded-md border border-dashed border-zinc-300 py-4 flex flex-col items-center gap-1 text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 transition-colors duration-150">
          {pyImport.isPending ? <CircleNotch size={18} className="animate-spin" /> : <UploadSimple size={18} />}
          <span className="text-[11px]">{pyImport.isPending ? "Reading your return…" : "Upload last year's return (PDF)"}</span>
        </button>
        <input ref={pyRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="gap-py-input"
          onChange={(e) => { if (e.target.files[0]) pyImport.mutate(e.target.files[0]); e.target.value = ""; }} />
      </div>
    );
  }
  const gaps = data.gaps || [];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="gap-panel">
      <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><GitDiff size={15} /> Cross-year gap check</h3>
      <p className="text-[11px] text-zinc-500 mb-3">Compared against your {data.profile_year} filed return — only what's actually missing, nothing redundant.</p>
      {gaps.length === 0 ? (
        <p className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle size={14} weight="fill" /> No gaps — everything from last year is accounted for.</p>
      ) : (
        <div className="space-y-2">
          {gaps.map((g) => (
            <div key={g.key} data-testid={`gap-${g.key}`} className="rounded-md border border-zinc-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-zinc-800">{g.label}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {g.days_open != null && (
                    <span data-testid={`gap-age-${g.key}`} className={`text-[9px] rounded-sm px-1.5 py-0.5 border ${g.days_open >= 7 ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-zinc-50 text-zinc-400 border-zinc-200"}`}>
                      open {g.days_open}d
                    </span>
                  )}
                  <span className={`text-[9px] uppercase rounded-sm px-1.5 py-0.5 border ${g.status === "Client_Unresolved" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {g.status === "Client_Unresolved" ? "Noted" : "Open"}
                  </span>
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-1">{g.prompt}</p>
              {g.reminded_at && <p data-testid={`gap-reminded-${g.key}`} className="text-[9px] text-zinc-400 mt-0.5">Reminder sent {new Date(g.reminded_at).toLocaleDateString()}</p>}
              {g.client_notes && <p className="text-[10px] text-indigo-700 mt-1 italic">Your note: {g.client_notes}</p>}
              <div className="flex gap-3 mt-1.5">
                <button data-testid={`gap-upload-btn-${g.key}`} onClick={() => { setPendingGap(g); inputRef.current?.click(); }}
                  className="flex items-center gap-1 text-[10px] text-zinc-700 underline hover:no-underline">
                  <UploadSimple size={11} /> Upload {g.upload_doc_type}
                </button>
                <button data-testid={`gap-note-btn-${g.key}`} onClick={() => { setNoteFor(noteFor === g.key ? null : g.key); setNote(g.client_notes || ""); }}
                  className="flex items-center gap-1 text-[10px] text-zinc-500 underline hover:no-underline">
                  <ChatText size={11} /> Explain instead
                </button>
              </div>
              {noteFor === g.key && (
                <div className="flex gap-2 mt-2">
                  <Input data-testid={`gap-note-input-${g.key}`} placeholder='e.g. "Sold this property in March — closing docs coming"' value={note} onChange={(e) => setNote(e.target.value)} className="h-7 text-xs" />
                  <Button data-testid={`gap-note-save-${g.key}`} size="sm" className="h-7 text-[11px]" disabled={!note.trim() || sendNote.isPending} onClick={() => sendNote.mutate(g)}>Save</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <input ref={inputRef} type="file" className="hidden" data-testid="gap-file-input"
        onChange={(e) => { if (e.target.files[0] && pendingGap) upload.mutate({ file: e.target.files[0], gap: pendingGap }); e.target.value = ""; }} />
    </div>
  );
}
